import { useEffect } from 'react';

const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  schema = null,
  breadcrumbs = null
}) => {
  const siteName = 'IZZISHOWIN';
  const defaultDescription = 'Premium film production services in Chicago. Rent exclusive filming locations, professional equipment, and hire experienced talent.';
  const defaultImage = 'https://izzishowin.com/images/hero/hero1.jpg';
  const baseUrl = 'https://izzishowin.com';

  const fullTitle = title ? `${title} | ${siteName}` : `${siteName} | Chicago Film Production Services`;
  const metaDescription = description || defaultDescription;
  const metaImage = image || defaultImage;
  const metaUrl = url ? `${baseUrl}${url}` : baseUrl;

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Helper to update or create meta tags
    const updateMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attr}="${name}"]`);

      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Update meta tags
    updateMeta('description', metaDescription);
    if (keywords) {
      updateMeta('keywords', keywords);
    }

    // Open Graph
    updateMeta('og:title', fullTitle, true);
    updateMeta('og:description', metaDescription, true);
    updateMeta('og:image', metaImage, true);
    updateMeta('og:url', metaUrl, true);
    updateMeta('og:type', type, true);
    updateMeta('og:site_name', siteName, true);

    // Twitter
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:title', fullTitle);
    updateMeta('twitter:description', metaDescription);
    updateMeta('twitter:image', metaImage);
    updateMeta('twitter:url', metaUrl);

    // Update canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', metaUrl);

    // Remove old schema scripts
    const oldSchemas = document.querySelectorAll('script[data-seo-schema]');
    oldSchemas.forEach(s => s.remove());

    // Add breadcrumbs schema
    if (breadcrumbs && breadcrumbs.length > 0) {
      const breadcrumbSchema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': breadcrumbs.map((crumb, index) => ({
          '@type': 'ListItem',
          'position': index + 1,
          'name': crumb.name,
          'item': `${baseUrl}${crumb.url}`
        }))
      };

      const breadcrumbScript = document.createElement('script');
      breadcrumbScript.type = 'application/ld+json';
      breadcrumbScript.setAttribute('data-seo-schema', 'breadcrumb');
      breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
      document.head.appendChild(breadcrumbScript);
    }

    // Add custom schema
    if (schema) {
      const schemaScript = document.createElement('script');
      schemaScript.type = 'application/ld+json';
      schemaScript.setAttribute('data-seo-schema', 'custom');
      schemaScript.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        ...schema
      });
      document.head.appendChild(schemaScript);
    }

    // Cleanup on unmount
    return () => {
      const schemas = document.querySelectorAll('script[data-seo-schema]');
      schemas.forEach(s => s.remove());
    };

  }, [fullTitle, metaDescription, metaImage, metaUrl, keywords, type, schema, breadcrumbs]);

  return null;
};

export default SEO;
